/**
 * Copilot Studio manual-auth SSO helpers.
 * @see https://learn.microsoft.com/en-us/microsoft-copilot-studio/configure-sso
 */
import type { DirectLine } from 'botframework-directlinejs';
import { azureADService } from '@services/azure-ad.service';

const OAUTH_CARD_CONTENT_TYPE = 'application/vnd.microsoft.card.oauth';

type OAuthActivity = {
  from?: { role?: string };
  attachments?: Array<{
    contentType?: string;
    content?: {
      connectionName?: string;
      tokenExchangeResource?: { id?: string; uri?: string };
    };
  }>;
};

export const getOAuthCardResourceUri = (activity: OAuthActivity): string | undefined => {
  const attachment = activity.attachments?.[0];
  if (
    attachment?.contentType === OAUTH_CARD_CONTENT_TYPE &&
    attachment.content?.tokenExchangeResource?.uri
  ) {
    return attachment.content.tokenExchangeResource.uri;
  }
  return undefined;
};

/**
 * Subscribe to Direct Line activities and silently exchange Entra ID tokens
 * for Copilot OAuth cards. Does not replace Web Chat's Redux store.
 */
export const subscribeCopilotSso = (
  directLine: DirectLine,
  userId: string,
  userName: string
): (() => void) => {
  const handledOAuthCards = new Set<string>();

  const subscription = directLine.activity$.subscribe((activity) => {
    const botActivity = activity as OAuthActivity;
    if (botActivity.from?.role !== 'bot') {
      return;
    }

    const resourceUri = getOAuthCardResourceUri(botActivity);
    if (!resourceUri) {
      return;
    }

    const oauthContent = botActivity.attachments?.[0]?.content;
    const exchangeId = oauthContent?.tokenExchangeResource?.id;
    if (!exchangeId || !oauthContent?.connectionName) {
      return;
    }

    if (handledOAuthCards.has(exchangeId)) {
      return;
    }
    handledOAuthCards.add(exchangeId);

    void (async () => {
      try {
        await azureADService.initialize();
        const token = await azureADService.acquireTokenForResource(resourceUri);
        if (!token) {
          return;
        }

        directLine
          .postActivity({
            type: 'invoke',
            name: 'signin/tokenExchange',
            value: {
              id: exchangeId,
              connectionName: oauthContent.connectionName,
              token,
            },
            from: { id: userId, name: userName, role: 'user' },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any)
          .subscribe();
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Copilot SSO token exchange failed:', error);
        }
      }
    })();
  });

  return () => subscription.unsubscribe();
};
